import {
  APPROVAL_WORKFLOW_VALIDATION,
  DESCRIPTION_VALIDATION,
  FORM_ACTION_VALIDATION,
  FORM_NAME_VALIDATION,
  TARGET_ENTITY_VALIDATION,
  URL_SLUG_VALIDATION,
} from './validationRules';

// Helper function to check for HTML tags
const containsHTML = (text: string): boolean => {
  const htmlRegex = /<[^>]*>/g;

  return htmlRegex.test(text);
};

// Helper function to trim leading/trailing whitespace
const trimWhitespace = (text: string): string => text.trim();

/**
 * Validates form name
 */
export const validateFormName = (name: string): string | null => {
  const trimmed = trimWhitespace(name);

  // Required validation
  if (!trimmed) {
    return FORM_NAME_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  // Max length validation
  if (trimmed.length > FORM_NAME_VALIDATION.MAX_LENGTH) {
    return FORM_NAME_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  // HTML validation
  if (containsHTML(trimmed)) {
    return FORM_NAME_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  // Character validation - allow letters, numbers, spaces, hyphens, underscores
  if (!FORM_NAME_VALIDATION.REGEX.test(trimmed)) {
    return FORM_NAME_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
  }

  // Must start with letter or number
  const firstChar = trimmed.charAt(0);

  if (!/^[a-zA-Z0-9]/.test(firstChar)) {
    return FORM_NAME_VALIDATION.ERROR_MESSAGES.INVALID_START;
  }

  return null;
};

/**
 * Validates form description
 */
export const validateDescription = (description: string): string | null => {
  // Description is optional, so empty is valid
  if (!description) {
    return null;
  }

  const trimmed = trimWhitespace(description);

  // Max length validation
  if (trimmed.length > DESCRIPTION_VALIDATION.MAX_LENGTH) {
    return DESCRIPTION_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  // HTML validation
  if (containsHTML(trimmed)) {
    return DESCRIPTION_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  // Character validation
  if (!DESCRIPTION_VALIDATION.REGEX.test(trimmed)) {
    return DESCRIPTION_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
  }

  return null;
};

/**
 * Validates URL slug
 * Enhanced from existing validateUrlSlug function
 */
export const validateUrlSlug = (slug: string): string | null => {
  const trimmed = trimWhitespace(slug);

  // Required validation
  if (!trimmed) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  // Max length validation
  if (trimmed.length > URL_SLUG_VALIDATION.MAX_LENGTH) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  // Spaces validation
  if (trimmed.includes(' ')) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.SPACES_NOT_ALLOWED;
  }

  // Leading/trailing hyphens validation
  if (trimmed.startsWith('-') || trimmed.endsWith('-')) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.LEADING_TRAILING_HYPHENS;
  }

  // Character validation - lowercase letters, numbers, hyphens only
  if (!URL_SLUG_VALIDATION.REGEX.test(trimmed)) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
  }

  // Reserved words validation - use type-safe approach
  const reservedWords = URL_SLUG_VALIDATION.RESERVED_WORDS;
  const lowerSlug = trimmed.toLowerCase();

  if (reservedWords.some((word) => word === lowerSlug)) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.RESERVED_WORD;
  }

  // Reserved prefix validation - URL slug cannot start with 'sav-da-'
  if (lowerSlug.startsWith(URL_SLUG_VALIDATION.RESERVED_PREFIX)) {
    return URL_SLUG_VALIDATION.ERROR_MESSAGES.RESERVED_PREFIX;
  }

  // Note: Uniqueness validation would require API call - handled separately
  return null;
};

/**
 * Validates target entity
 */
export const validateTargetEntity = (
  category: string | undefined
): string | null => {
  if (category === undefined) {
    return TARGET_ENTITY_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  const trimmed = trimWhitespace(category);

  // Required validation
  if (!trimmed) {
    return TARGET_ENTITY_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  // Valid category validation - use type-safe approach
  const validCategories = TARGET_ENTITY_VALIDATION.VALID_CATEGORIES;

  // Added toLowerCase to avoid case sensitivity as part of EXT-1797
  if (
    !validCategories.some(
      (validCategory) => validCategory?.toLowerCase() === trimmed?.toLowerCase()
    )
  ) {
    return TARGET_ENTITY_VALIDATION.ERROR_MESSAGES.INVALID_SELECTION;
  }

  return null;
};

/**
 * Validates form action
 */
export const validateFormAction = (
  action: string | undefined,
  category?: string
): string | null => {
  if (action === undefined) {
    return FORM_ACTION_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  const trimmedAction = trimWhitespace(action);

  // Required validation
  if (!trimmedAction) {
    return FORM_ACTION_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  // Category-specific validation
  if (category) {
    const validActionsForCategory =
      FORM_ACTION_VALIDATION.VALID_ACTIONS[
        category as keyof typeof FORM_ACTION_VALIDATION.VALID_ACTIONS
      ];

    // TODO: Not needed now when there will be more than 1 catergory then we can use it.
    // if (!validActionsForCategory) {
    //   return FORM_ACTION_VALIDATION.ERROR_MESSAGES.INVALID_SELECTION;
    // }

    if (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      validActionsForCategory &&
      !validActionsForCategory.some(
        (validAction) => validAction === trimmedAction
      )
    ) {
      return FORM_ACTION_VALIDATION.ERROR_MESSAGES.CATEGORY_MISMATCH;
    }
  }

  return null;
};

/**
 * Validates approval workflow
 */
export const validateApprovalWorkflow = (
  workflow: string | undefined
): string | null => {
  if (workflow === undefined) {
    return APPROVAL_WORKFLOW_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  const trimmed = trimWhitespace(workflow);

  // Required validation
  if (!trimmed) {
    return APPROVAL_WORKFLOW_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  // Note: Active workflow validation would require API call - handled separately
  return null;
};
